import { ContentSwitcherValue } from '../../types';

import { getRelatedParams } from './getRelatedParams';

export const getActiveItems = (
  values: ContentSwitcherValue[],
  sessionSettings: string[],
): Record<string, string> => {
  if (!values.length) {
    return {};
  }

  // a list of values that should be displayed based on child relations
  // a child is not always the next array index
  // so we need to collect them while going through the array
  let activeValues: string[] = [values[0].param];
  const activeItems: Record<string, string> = values.reduce(
    (arr: Record<string, string>, { param: valueParam }, i) => {
      if (i === 0) {
        // note: we don't assume that the first paramValue is a child of another paramValue
        // check the sessionSettings to check if there is already a preferred value
        const index = sessionSettings.findIndex(lang =>
          values[i].items.some(({ id }) => id === lang),
        );
        if (index !== -1) {
          return { ...arr, [valueParam]: sessionSettings[index] };
        }

        // try to find a value based on parent-child relationships
        const parent = values[i].items.find(child => {
          if (child.children) {
            const childValues = Object.values(child.children).flat();
            return sessionSettings.some(lang =>
              childValues.some(id => id === lang),
            );
          }
          return false;
        });

        if (parent) {
          return { ...arr, [valueParam]: parent.id };
        }

        if (values[i]?.items[0]) {
          // use the first item as default if no paramValue was found
          return { ...arr, [valueParam]: values[i].items[0].id };
        }
        return arr;
      }

      // get all related params
      const relatedParams = Object.entries(arr).reduce<ContentSwitcherValue[]>(
        (agg, [key, value]) => {
          const related = getRelatedParams(
            agg.length ? agg : values,
            key,
            value,
          );

          return related;
        },
        [],
      );

      // add the child relations to the list
      activeValues = [
        ...new Set([
          ...relatedParams.map(({ param }) => param),
          ...activeValues,
        ]),
      ];

      const value = relatedParams.find(({ param }) => param === valueParam);

      const possibleItems = value?.items;

      // get first option from related params
      if (possibleItems?.length) {
        // check for a previous selected value
        const index = sessionSettings.findIndex(lang =>
          possibleItems.some(({ id }) => id === lang),
        );
        if (index !== -1) {
          return { ...arr, [valueParam]: sessionSettings[index] };
        }

        return { ...arr, [valueParam]: possibleItems[0].id };
      }

      // check for a previous selected value
      const index = sessionSettings.findIndex(lang =>
        values[i].items.some(({ id }) => id === lang),
      );
      if (index !== -1) {
        return { ...arr, [valueParam]: sessionSettings[index] };
      }

      // use the first item as default
      if (values[i].items[0]) {
        return { ...arr, [valueParam]: values[i].items[0].id };
      }

      return arr;
    },
    {},
  );

  // filter the result based on the relations
  return activeValues.reduce((arr: Record<string, string>, activeValue) => {
    if (activeItems[activeValue]) {
      return { ...arr, [activeValue]: activeItems[activeValue] };
    }
    return arr;
  }, {});
};
